/**
 * Lab commission-report member chips — typeahead via getChildUser, removable tags.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Chip, Field, IconButton, Input, Spinner } from '@w7/ui'
import { useCommissionChildUsers } from '@/api/hooks/reports/useCommissionChildUsers'
import type { CommissionChildUser } from '@/api/map/commissionEligibility'

export type CommissionMemberPickerProps = {
  selected: CommissionChildUser[]
  onChange: (next: CommissionChildUser[]) => void
  tripleMaster?: string
  doubleMaster?: string
  superMaster?: string
  master?: string
}

const DEBOUNCE_MS = 150
const SUGGEST_LIMIT = 20

export function CommissionMemberPicker({
  selected,
  onChange,
  tripleMaster,
  doubleMaster,
  superMaster,
  master,
}: CommissionMemberPickerProps) {
  const listId = useId()
  const wrapRef = useRef<HTMLDivElement>(null)
  const [draft, setDraft] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(draft.trim()), DEBOUNCE_MS)
    return () => window.clearTimeout(t)
  }, [draft])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (wrapRef.current?.contains(event.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const searchQ = useCommissionChildUsers(
    {
      player: debounced,
      tripleMaster,
      doubleMaster,
      superMaster,
      master,
    },
    open && debounced.length >= 1,
  )

  const selectedIds = useMemo(() => new Set(selected.map((m) => m.id)), [selected])
  const suggestions = useMemo(
    () =>
      (searchQ.data ?? [])
        .filter((row) => !selectedIds.has(row.id))
        .slice(0, SUGGEST_LIMIT),
    [searchQ.data, selectedIds],
  )

  const pick = (row: CommissionChildUser) => {
    if (selectedIds.has(row.id)) return
    onChange([...selected, row])
    setDraft('')
    setDebounced('')
    setOpen(false)
  }

  const remove = (id: string) => onChange(selected.filter((m) => m.id !== id))

  return (
    <div className="cm-commission-member" ref={wrapRef}>
      <Field label="Member">
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            aria-autocomplete="list"
            aria-controls={listId}
            aria-expanded={open}
            role="combobox"
            placeholder="Enter or Select Member Name to Search"
            value={draft}
            autoComplete="off"
            onChange={(e) => {
              setDraft(e.target.value)
              setOpen(true)
            }}
            onFocus={() => setOpen(true)}
          />
        )}
      </Field>

      {open && debounced.length >= 1 ? (
        <ul
          id={listId}
          className="cm-commission-member-suggest"
          role="listbox"
          aria-label="Matching players"
        >
          {searchQ.isFetching && suggestions.length === 0 ? (
            <li className="cm-commission-member-empty" role="presentation">
              <Spinner size={14} /> Searching…
            </li>
          ) : null}
          {!searchQ.isFetching && suggestions.length === 0 ? (
            <li className="cm-commission-member-empty">No players found</li>
          ) : null}
          {suggestions.map((row) => (
            <li key={row.id} role="option">
              <button type="button" className="cm-commission-member-option" onClick={() => pick(row)}>
                {row.username}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {selected.length > 0 ? (
        <div className="cm-commission-member-chips w7-chiprow" aria-label="Selected members">
          {selected.map((m) => (
            <Chip key={m.id} tone="warn" size="sm" className="cm-commission-member-chip">
              {m.username}
              <IconButton
                name="close"
                size="sm"
                label={`Remove ${m.username}`}
                onClick={() => remove(m.id)}
              />
            </Chip>
          ))}
        </div>
      ) : null}
    </div>
  )
}
